import { AppBarData } from "@/appBar/appBarDomain";
import { LocalizedText } from "@/common/common";
import { CustomRole } from "@/common/customRole";
import { DecisionType } from "@/common/decisions";
import { findInitialUrl } from "@/common/customRoleNavigation";
import { QuickFilter, QuickFilterBarProps } from "@/components-v2/QuickFilterBar";
import { TabsProps } from "@/components-v2/Tabs";
import { ProjectDecisionItemDTO } from "@/dbot/common/commonDomain";
import { projectApi } from "@/dbot/common/projectApi";
import {
  DCEvent,
  EventDispatcher,
  navigate,
  showGeneralError,
  UpdateStoreEvent,
} from "@/events/eventsDomain";
import { Language } from "@/i18n/languages";
import { TFunction, TMessage } from "@/i18n/tt";
import { TMessageKey } from "@/i18n/types.gen";
import { accessTypeLabel } from "@/portfolio/contacts/customRoleDomain";
import {
  extractAggregatedCompanyRoleDetailsUrl,
  extractAggregatedShareholderDetailsUrl,
} from "@/portfolio/contacts/contactsDomain";
import {
  extractPortfolioCompaniesUrl,
  extractPortfolioInstanceUrl,
  PortfolioAllCompanies,
  PortfolioInstance,
  portfolioMyInvestments,
  PortfolioType,
  portfolioWithAdminRole,
  portfolioWithBoardRole,
  SelectedPortfolioOption,
} from "@/portfolio/portfolioDomain";
import AlertMessageStore from "@/stores/AlertMessageStore";
import { AccessType } from "@/types/enums/AccessType";
import { adminProject, companyUrl, customRoleCompanyUrl } from "@/utils/navigation";
import { when } from "@/utils/functions";
import { resolveUrnReference } from "@/utils/UrnReference";
import { groupBy, isFunction, orderBy, sortBy, uniqBy } from "lodash";
import {
  CompanyAccessDTO,
  CompanyDTO,
  ContactDTO,
  PortfolioDTO,
  PortfolioOverviewDTO,
  portfolioV2ApiClient,
  RecentProject,
} from "./portfolioV2ApiClient";

export type RecentTab = "companies" | "starred" | "projects" | "matters";

export type QuickSwitcherTab = "all" | "companies" | "contacts";

export type PortfolioSearchState = {
  isLoading?: boolean;
  overview?: PortfolioOverviewDTO;
  recentMatters?: ProjectDecisionItemDTO[];
  quickSwitcherTab?: QuickSwitcherTab;
  isQuickSwitcherOpen?: boolean;
  recentTab?: RecentTab;
};

const RECENT_ITEMS_LIMIT = 5;

export type QuickSwitcherItemType = "company" | "contact";

export type CompanyQuickSwitcherItem = {
  type: "company";
  name: string;
  portfolioLabels: TMessage[];
  access: CompanyAccessDTO;
  customRole?: CustomRole;
  lastVisitedAt?: string;
};

export type ContactQuickSwitcherItem = {
  type: "contact";
  name: string;
  portfolioLabels: TMessage[];
  contactId: string;
};

export type QuickSwitcherItem = CompanyQuickSwitcherItem | ContactQuickSwitcherItem;

export type RecentProjectItem = {
  type: "project";
  projectId: string;
  orgNo: string;
  name: string;
  companyName: string;
  updatedAt: string;
};

export type RecentMatterItem = {
  type: "matter";
  decisionId: string;
  decisionType: DecisionType;
  titles?: LocalizedText[];
  projectId: string;
  orgNo: string;
  projectName: string;
  companyName: string;
  updatedAt: string;
};

export type RecentPanelItem = CompanyQuickSwitcherItem | RecentProjectItem | RecentMatterItem;

type RoleBasedPortfolioType = Exclude<PortfolioType, "all-companies" | "portfolio-instance">;

type RolePortfolioOption = Exclude<
  SelectedPortfolioOption,
  PortfolioAllCompanies | PortfolioInstance
>;

export type QuickFilterScope =
  | RolePortfolioOption
  | { type: "portfolio-instance"; portfolio: PortfolioDTO };

const portfolioAccessTypes: Record<RoleBasedPortfolioType, AccessType[]> = {
  "with-admin-role": [AccessType.ADMIN],
  "with-board-role": [AccessType.BOARD_MEMBER],
  "my-investments": [AccessType.SHAREHOLDER, AccessType.ADDITIONAL_REPRESENTATIVE],
};

export const orgNosInPortfolio = (
  overview: PortfolioOverviewDTO,
  portfolio: SelectedPortfolioOption | QuickFilterScope,
): Set<string> => {
  switch (portfolio.type) {
    case "all-companies":
      return new Set(overview.companies.map((c) => c.orgNo));
    case "portfolio-instance":
      return new Set(
        overview.portfolioCompanies
          .filter((link) => link.portfolioId === portfolio.portfolio.id)
          .map((link) => link.orgNo),
      );
    case "with-admin-role":
    case "with-board-role":
    case "my-investments": {
      const accessTypes = portfolioAccessTypes[portfolio.type];
      return new Set(
        overview.companyAccess
          .filter((access) => accessTypes.includes(access.accessType))
          .map((access) => access.orgNo),
      );
    }
  }
};

export const contactsInPortfolio = (
  overview: PortfolioOverviewDTO,
  orgNos: Set<string>,
): ContactDTO[] => {
  const contactIds = new Set(
    [...overview.companyRoles, ...overview.ownerships]
      .filter((link) => orgNos.has(link.orgNo))
      .map((link) => link.contactId),
  );
  return overview.contacts.filter((c) => contactIds.has(c.contactId));
};

const accessPriority: AccessType[] = [
  AccessType.ADMIN,
  AccessType.CUSTOM,
  AccessType.BOARD_MEMBER,
  AccessType.SHAREHOLDER,
  AccessType.ADDITIONAL_REPRESENTATIVE,
];

const accessTypesShownInPortfolio = (portfolio: SelectedPortfolioOption): AccessType[] => {
  switch (portfolio.type) {
    case "all-companies":
    case "portfolio-instance":
      return accessPriority;
    case "with-admin-role":
    case "with-board-role":
    case "my-investments":
      return portfolioAccessTypes[portfolio.type];
  }
};

export const companyAccessInPortfolio = (
  overview: PortfolioOverviewDTO,
  portfolio: SelectedPortfolioOption,
  orgNos: Set<string>,
): CompanyAccessDTO[] => {
  const shown = accessTypesShownInPortfolio(portfolio);
  const accessByOrgNo = groupBy(
    overview.companyAccess.filter((a) => orgNos.has(a.orgNo) && shown.includes(a.accessType)),
    (a) => a.orgNo,
  );
  return overview.companies.flatMap((company) =>
    sortBy(accessByOrgNo[company.orgNo] ?? [], (a) => accessPriority.indexOf(a.accessType)),
  );
};

const implicitPortfolios: SelectedPortfolioOption[] = [
  portfolioWithAdminRole,
  portfolioWithBoardRole,
  portfolioMyInvestments,
];

const isInImplicitPortfolio = (
  portfolio: SelectedPortfolioOption,
  accessTypes: Set<AccessType>,
): boolean => {
  switch (portfolio.type) {
    case "with-admin-role":
    case "with-board-role":
    case "my-investments":
      return portfolioAccessTypes[portfolio.type].some((type) => accessTypes.has(type));
    default:
      return false;
  }
};

export type PortfolioOverviewIndex = {
  overview: PortfolioOverviewDTO;
  companiesByOrgNo: Map<string, CompanyDTO>;
  customRolesById: Map<string, CustomRole>;
  accessTypesByOrgNo: Map<string, AccessType[]>;
  portfolioIdsByOrgNo: Map<string, string[]>;
  orgNosByContactId: Map<string, string[]>;
};

const groupValues = <T, V>(
  items: T[],
  keyFn: (item: T) => string,
  valueFn: (item: T) => V,
): Map<string, V[]> =>
  new Map(Object.entries(groupBy(items, keyFn)).map(([key, group]) => [key, group.map(valueFn)]));

export const indexOverview = (overview: PortfolioOverviewDTO): PortfolioOverviewIndex => ({
  overview,
  companiesByOrgNo: new Map(overview.companies.map((c) => [c.orgNo, c])),
  customRolesById: new Map(overview.customRoles.map((r) => [r.customRoleId, r])),
  accessTypesByOrgNo: groupValues(
    overview.companyAccess,
    (a) => a.orgNo,
    (a) => a.accessType,
  ),
  portfolioIdsByOrgNo: groupValues(
    overview.portfolioCompanies,
    (link) => link.orgNo,
    (link) => link.portfolioId,
  ),
  orgNosByContactId: groupValues(
    [...overview.companyRoles, ...overview.ownerships],
    (link) => link.contactId,
    (link) => link.orgNo,
  ),
});

export const portfolioLabelsOfCompanies = (
  index: PortfolioOverviewIndex,
  orgNos: string[],
): TMessage[] => {
  if (orgNos.length === 0) return [];
  const accessTypes = new Set(orgNos.flatMap((orgNo) => index.accessTypesByOrgNo.get(orgNo) ?? []));
  const portfolioIds = new Set(
    orgNos.flatMap((orgNo) => index.portfolioIdsByOrgNo.get(orgNo) ?? []),
  );
  return [
    ...implicitPortfolios
      .filter((p) => p !== portfolioWithAdminRole && isInImplicitPortfolio(p, accessTypes))
      .map((p) => p.label),
    ...index.overview.portfolios
      .filter((p) => portfolioIds.has(p.id))
      .map((p) => ({ translated: p.name })),
  ];
};

const toCompanyItems = (
  index: PortfolioOverviewIndex,
  access: CompanyAccessDTO[],
): CompanyQuickSwitcherItem[] =>
  access.map((a) => ({
    type: "company",
    name: index.companiesByOrgNo.get(a.orgNo)?.name ?? "",
    portfolioLabels: portfolioLabelsOfCompanies(index, [a.orgNo]),
    access: a,
    customRole: when(a.customRoleId, (id) => index.customRolesById.get(id)),
    lastVisitedAt: index.companiesByOrgNo.get(a.orgNo)?.lastVisitedAt,
  }));

const toContactItems = (
  index: PortfolioOverviewIndex,
  contacts: ContactDTO[],
): ContactQuickSwitcherItem[] =>
  contacts.map((contact) => ({
    type: "contact",
    name: contact.name,
    portfolioLabels: portfolioLabelsOfCompanies(
      index,
      index.orgNosByContactId.get(contact.contactId) ?? [],
    ),
    contactId: contact.contactId,
  }));

export const quickSwitcherItems = (
  overview: PortfolioOverviewDTO | undefined,
  portfolio: SelectedPortfolioOption,
): QuickSwitcherItem[] => {
  if (!overview) return [];
  const index = indexOverview(overview);
  const orgNos = orgNosInPortfolio(overview, portfolio);
  return [
    ...toCompanyItems(index, companyAccessInPortfolio(overview, portfolio, orgNos)),
    ...toContactItems(index, contactsInPortfolio(overview, orgNos)),
  ];
};

const isInQuickSwitcherTab = (item: QuickSwitcherItem, tab: QuickSwitcherTab): boolean => {
  switch (tab) {
    case "all":
      return true;
    case "companies":
      return item.type === "company";
    case "contacts":
      return item.type === "contact";
  }
};

export const quickSwitcherItemsInTab = (items: QuickSwitcherItem[], tab: QuickSwitcherTab) =>
  items.filter((item) => isInQuickSwitcherTab(item, tab));

export const activeQuickSwitcherTab = (
  state: PortfolioSearchState | undefined,
  items: QuickSwitcherItem[],
): QuickSwitcherTab => {
  const tab = state?.quickSwitcherTab ?? "all";
  return quickSwitcherItemsInTab(items, tab).length > 0 ? tab : "all";
};

const quickSwitcherTabs: QuickSwitcherTab[] = ["all", "companies", "contacts"];

const quickSwitcherTabLabels: Record<QuickSwitcherTab, TMessage> = {
  all: { key: "portfolio.search.tabs.all" },
  companies: { key: "portfolio.search.tabs.companies" },
  contacts: { key: "portfolio.search.tabs.contacts" },
};

export const prepQuickSwitcherTabs = (
  state: PortfolioSearchState | undefined,
  items: QuickSwitcherItem[],
): TabsProps => {
  const active = activeQuickSwitcherTab(state, items);
  return {
    tabs: quickSwitcherTabs.map((tab) => ({
      id: tab,
      label: quickSwitcherTabLabels[tab],
      isActive: active === tab,
      disabled: quickSwitcherItemsInTab(items, tab).length === 0,
      onSelect: [setInPortfolioSearchState("quickSwitcherTab", tab)],
    })),
    scrollable: false,
  };
};

const companyItems = (items: QuickSwitcherItem[]): CompanyQuickSwitcherItem[] =>
  items.filter((item) => item.type === "company");

export const recentCompanyItems = (items: QuickSwitcherItem[]): CompanyQuickSwitcherItem[] => {
  const visited = companyItems(items).filter((item) => item.lastVisitedAt);
  return orderBy(
    uniqBy(visited, (item) => item.access.orgNo),
    (item) => item.lastVisitedAt,
    "desc",
  ).slice(0, RECENT_ITEMS_LIMIT);
};

export const starredCompanyItems = (items: QuickSwitcherItem[]): CompanyQuickSwitcherItem[] =>
  sortBy(
    companyItems(items).filter((item) => item.access.isStarred),
    (item) => item.name.toLocaleLowerCase(),
  );

const toProjectItem = (project: RecentProject, companyName: string): RecentProjectItem => ({
  type: "project",
  projectId: project.projectId,
  orgNo: project.orgNo,
  name: project.name,
  companyName,
  updatedAt: project.updatedAt,
});

const toMatterItem = (matter: ProjectDecisionItemDTO, companyName: string): RecentMatterItem => ({
  type: "matter",
  decisionId: matter.decisionId,
  decisionType: matter.decisionType,
  titles: matter.titles,
  projectId: matter.projectId,
  orgNo: matter.orgNo,
  projectName: matter.projectName,
  companyName,
  updatedAt: matter.updatedAt,
});

const recentItemsInScope = <T extends { orgNo: string }, R extends { updatedAt: string }>(
  items: QuickSwitcherItem[],
  candidates: T[],
  toItem: (candidate: T, companyName: string) => R,
): R[] => {
  const companyNames = new Map(companyItems(items).map((item) => [item.access.orgNo, item.name]));
  const inScope = candidates.flatMap((candidate) => {
    const companyName = companyNames.get(candidate.orgNo);
    return companyName === undefined ? [] : [toItem(candidate, companyName)];
  });
  return orderBy(inScope, (item) => item.updatedAt, "desc").slice(0, RECENT_ITEMS_LIMIT);
};

export const recentProjectItems = (
  overview: PortfolioOverviewDTO | undefined,
  items: QuickSwitcherItem[],
): RecentProjectItem[] =>
  overview ? recentItemsInScope(items, overview.recentProjects, toProjectItem) : [];

export const recentMatterItems = (
  recentMatters: ProjectDecisionItemDTO[] | undefined,
  items: QuickSwitcherItem[],
): RecentMatterItem[] => recentItemsInScope(items, recentMatters ?? [], toMatterItem);

const recentTabs: RecentTab[] = ["companies", "starred", "projects", "matters"];

const recentActivityTabs: RecentTab[] = ["companies", "projects", "matters"];

const recentItemsInTab = (
  tab: RecentTab,
  state: PortfolioSearchState | undefined,
  items: QuickSwitcherItem[],
): RecentPanelItem[] => {
  switch (tab) {
    case "companies":
      return recentCompanyItems(items);
    case "projects":
      return recentProjectItems(state?.overview, items);
    case "matters":
      return recentMatterItems(state?.recentMatters, items);
    case "starred":
      return starredCompanyItems(items);
  }
};

const hasRecentItems = (
  tab: RecentTab,
  state: PortfolioSearchState | undefined,
  items: QuickSwitcherItem[],
): boolean => recentItemsInTab(tab, state, items).length > 0;

export const activeRecentTab = (
  state: PortfolioSearchState | undefined,
  items: QuickSwitcherItem[],
): RecentTab => {
  const tab = state?.recentTab ?? "companies";
  return hasRecentItems(tab, state, items)
    ? tab
    : (recentTabs.find((t) => hasRecentItems(t, state, items)) ?? "companies");
};

export const recentPanelItems = (
  state: PortfolioSearchState | undefined,
  items: QuickSwitcherItem[],
): RecentPanelItem[] => recentItemsInTab(activeRecentTab(state, items), state, items);

export const recentPanelItemKey = (item: RecentPanelItem): string => {
  switch (item.type) {
    case "company":
      return `${item.access.orgNo}-${item.access.accessType}`;
    case "project":
      return item.projectId;
    case "matter":
      return item.decisionId;
  }
};

const recentTabLabels: Record<RecentTab, TMessageKey> = {
  companies: "portfolio.search.recent.tabs.companies",
  projects: "portfolio.search.recent.tabs.projects",
  matters: "portfolio.search.recent.tabs.matters",
  starred: "portfolio.search.recent.tabs.starred",
};

export const showRecentPanel = (
  state: PortfolioSearchState | undefined,
  items: QuickSwitcherItem[],
): boolean => recentActivityTabs.some((tab) => hasRecentItems(tab, state, items));

export const prepRecentTabs = (
  state: PortfolioSearchState | undefined,
  items: QuickSwitcherItem[],
): TabsProps => ({
  tabs: recentTabs.map((tab) => ({
    id: tab,
    label: { key: recentTabLabels[tab] },
    isActive: activeRecentTab(state, items) === tab,
    disabled: !hasRecentItems(tab, state, items),
    onSelect: [setInPortfolioSearchState("recentTab", tab)],
  })),
  scrollable: false,
});

export const quickSwitcherItemText = (
  item: QuickSwitcherItem,
  t: TFunction,
  language: Language,
): string[] => {
  switch (item.type) {
    case "company":
      return [
        item.name,
        t(accessTypeLabel(item.access.accessType, item.customRole, language)),
        ...item.portfolioLabels.map((label) => t(label)),
      ];
    case "contact":
      return [item.name, ...item.portfolioLabels.map((label) => t(label))];
  }
};

export const companyAccessUrl = (
  access: CompanyAccessDTO,
  customRole: CustomRole | undefined,
): string =>
  access.accessType === AccessType.CUSTOM && customRole
    ? customRoleCompanyUrl({
        baseUrl: findInitialUrl({ role: customRole, orgNo: access.orgNo }),
        roleId: customRole.customRoleId,
      })
    : companyUrl(access.orgNo, access.accessType, access.shareholderId);

export const contactUrl = (
  contactId: string,
  portfolio: SelectedPortfolioOption,
): string | undefined => {
  const ref = resolveUrnReference(contactId);
  switch (ref?.type) {
    case "companyroleId":
      return extractAggregatedCompanyRoleDetailsUrl(portfolio, ref.id);
    case "shareholderId":
      return extractAggregatedShareholderDetailsUrl(portfolio, ref.id);
    default:
      return undefined;
  }
};

export const quickSwitcherItemUrl = (
  item: QuickSwitcherItem,
  portfolio: SelectedPortfolioOption,
): string | undefined => {
  switch (item.type) {
    case "company":
      return companyAccessUrl(item.access, item.customRole);
    case "contact":
      return contactUrl(item.contactId, portfolio);
  }
};

export const setQuickSwitcherOpen = (open: boolean): UpdateStoreEvent =>
  setInPortfolioSearchState("isQuickSwitcherOpen", open);

export const selectQuickSwitcherItem = (
  item: QuickSwitcherItem,
  overview: PortfolioOverviewDTO | undefined,
  portfolio: SelectedPortfolioOption,
): DCEvent[] => {
  if (!overview) return [];
  const url = quickSwitcherItemUrl(item, portfolio);
  return url ? [navigate(url)] : [];
};

export const selectRecentPanelItem = (
  item: RecentPanelItem,
  overview: PortfolioOverviewDTO | undefined,
  portfolio: SelectedPortfolioOption,
): DCEvent[] => {
  switch (item.type) {
    case "company":
      return selectQuickSwitcherItem(item, overview, portfolio);
    case "project":
    case "matter":
      return [navigate(adminProject({ orgNo: item.orgNo, projectId: item.projectId }))];
  }
};

const quickFilterScopes = (overview: PortfolioOverviewDTO): QuickFilterScope[] => [
  portfolioWithAdminRole,
  portfolioWithBoardRole,
  portfolioMyInvestments,
  ...sortBy(overview.portfolios, (p) => p.name).map((portfolio) => ({
    type: "portfolio-instance" as const,
    portfolio,
  })),
];

const quickFilterId = (scope: QuickFilterScope): string =>
  scope.type === "portfolio-instance" ? scope.portfolio.id : scope.type;

const quickFilterLabel = (scope: QuickFilterScope): TMessage =>
  scope.type === "portfolio-instance" ? { translated: scope.portfolio.name } : scope.label;

export const quickFilterUrl = (scope: QuickFilterScope): string =>
  scope.type === "portfolio-instance"
    ? `${extractPortfolioInstanceUrl(scope.portfolio.id)}/companies`
    : extractPortfolioCompaniesUrl(scope);

const quickFilterTooltipKey = (scope: QuickFilterScope): TMessageKey => {
  switch (scope.type) {
    case "portfolio-instance":
      return "portfolio.quick-filters.tooltips.instance";
    case "with-admin-role":
      return "portfolio.quick-filters.tooltips.admin";
    case "with-board-role":
      return "portfolio.quick-filters.tooltips.board";
    case "my-investments":
      return "portfolio.quick-filters.tooltips.investments";
  }
};

export const quickFilterTooltip = (
  scope: QuickFilterScope,
  overview: PortfolioOverviewDTO,
  orgNos: Set<string>,
): TMessage => ({
  key: quickFilterTooltipKey(scope),
  params: {
    count: orgNos.size,
    companyName:
      orgNos.size === 1 ? overview.companies.find((c) => orgNos.has(c.orgNo))?.name : undefined,
    portfolioName: scope.type === "portfolio-instance" ? scope.portfolio.name : undefined,
  },
});

export const portfolioQuickFilters = (
  overview: PortfolioOverviewDTO | undefined,
): QuickFilter[] => {
  if (!overview) return [];
  const scopes = quickFilterScopes(overview)
    .map((scope) => ({ scope, orgNos: orgNosInPortfolio(overview, scope) }))
    .filter(({ orgNos }) => orgNos.size > 0);
  if (scopes.length < 2) return [];
  return scopes.map(({ scope, orgNos }) => ({
    id: quickFilterId(scope),
    label: quickFilterLabel(scope),
    onSelect: [navigate(quickFilterUrl(scope))],
    tooltip: quickFilterTooltip(scope, overview, orgNos),
  }));
};

export const prepQuickFilterBar = (
  state: PortfolioSearchState | undefined,
): QuickFilterBarProps => ({
  filters: portfolioQuickFilters(state?.overview),
  tooltipMessage: { key: "portfolio.quick-filters.more" },
  wrap: true,
});

export const QUICK_SWITCHER_SHORTCUT_KEY = "k";

export type QuickSwitcherContext = {
  portfolio: SelectedPortfolioOption;
  dispatch: EventDispatcher;
  t: TFunction;
  language: Language;
};

export type PortfolioSearchContext = QuickSwitcherContext & {
  appBar: AppBarData | null;
  alertStore: AlertMessageStore;
};

export const initPortfolioSearchPage = (): DCEvent[] => [
  setInPortfolioSearchState("isLoading", (state) => !state.overview),
  loadOverview(),
];

const loadOverview = (): DCEvent =>
  portfolioV2ApiClient.getOverview({
    onSuccess: (res) => [
      updatePortfolioSearchState({ isLoading: false, overview: res.data }),
      ...(hasAdminAccess(res.data) ? [loadRecentMatters()] : []),
    ],
    onFailure: () => [setInPortfolioSearchState("isLoading", false), showGeneralError()],
  });

const hasAdminAccess = (overview: PortfolioOverviewDTO): boolean =>
  overview.companyAccess.some((access) => access.accessType === AccessType.ADMIN);

const loadRecentMatters = (): DCEvent =>
  projectApi.getRecentMatters({
    onSuccess: (res) => [setInPortfolioSearchState("recentMatters", res.data.items)],
    onFailure: () => [showGeneralError()],
  });

export const updatePortfolioSearchState = (
  changes: Partial<PortfolioSearchState>,
): UpdateStoreEvent => ({
  type: "update-store",
  data: { portfolioSearch: (current = {}) => ({ ...current, ...changes }) },
});

const setInPortfolioSearchState = <K extends keyof PortfolioSearchState>(
  key: K,
  valueOrCallback:
    | PortfolioSearchState[K]
    | ((current: PortfolioSearchState) => PortfolioSearchState[K]),
): UpdateStoreEvent => ({
  type: "update-store",
  data: {
    portfolioSearch: (state = {}) => ({
      ...state,
      [key]: isFunction(valueOrCallback) ? valueOrCallback(state) : valueOrCallback,
    }),
  },
});
